// hooks/finance/usePaymentRequestsBulk.ts
import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  PaymentRequest,
  PaymentRequestStatus,
} from '@/types/database/payment-requests';
import {
  approvePaymentRequestAction,
  rejectPaymentRequestAction,
  confirmPaymentAction,
} from './usePaymentRequestActions';
import { sendTelegramNotification } from '@/services/telegram/send';

type BulkStatus = PaymentRequestStatus | 'mixed';

type BulkApproveInput = {
  requests: PaymentRequest[];
  comments?: string;
};

type BulkRejectInput = {
  requests: PaymentRequest[];
  reason: string;
  comments?: string;
};

type BulkConfirmPaymentItem = {
  id: string;
  paymentReference?: string;
  actualPaymentDate?: string;
  bankAccountId: string;
  baseAmount: number;
  vatRate: number;
  vatAmount: number;
  totalExpense: number;
  withholdingRate: number;
  withholdingAmount: number;
  netPayment: number;
};

type BulkConfirmPaymentRequestsInput = {
  payments: BulkConfirmPaymentItem[];
};

export type BulkPaymentRequestGroup = {
  uploadId: string;
  uploadMethod: string | null;
  createdAt: string;
  requestsCount: number;
  totalAmount: number;
  currency: string | null;
  department: PaymentRequest['department'] | null;
  departmentId: string | null;
  categories: Array<{ id: string; name: string }>;
  categoryIds: string[];
  searchIndex: string;
  requests: PaymentRequest[];
  firstRequest: PaymentRequest;
  bulkStatus: BulkStatus;
};

const buildBulkGroup = (
  requests: PaymentRequest[]
): BulkPaymentRequestGroup | null => {
  if (!requests.length) return null;

  const firstRequest = requests[0];
  const statuses = [...new Set(requests.map((r) => r.status))];
  const bulkStatus: BulkStatus =
    statuses.length === 1 ? statuses[0] : 'mixed';

  const categoriesMap = new Map<string, { id: string; name: string }>();

  for (const request of requests) {
    const category = request.category;
    if (category?.id && category?.name) {
      categoriesMap.set(category.id, {
        id: category.id,
        name: category.name,
      });
    }
  }

  const categories = [...categoriesMap.values()];
  const categoryIds = [...categoriesMap.keys()];

  const searchIndex = [
    firstRequest.upload_id,
    firstRequest.upload_method,
    firstRequest.department?.name,
    ...categories.map((c) => c.name),
    ...requests.flatMap((r) => [
      r.request_number,
      r.title,
      r.vendor_name,
      r.description,
      r.amount?.toString(),
      r.total_amount?.toString(),
    ]),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return {
    uploadId: firstRequest.upload_id,
    uploadMethod: firstRequest.upload_method ?? null,
    createdAt: firstRequest.created_at,
    requestsCount: requests.length,
    totalAmount: requests.reduce((sum, request) => sum + (request.total_amount || 0), 0),
    currency: firstRequest.currency ?? null,
    department: firstRequest.department ?? null,
    departmentId: firstRequest.department_id ?? null,
    categories,
    categoryIds,
    searchIndex,
    requests,
    firstRequest,
    bulkStatus,
  };
};

const invalidatePaymentRequestQueries = async (queryClient: QueryClient) => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['payment-requests'] }),
    queryClient.invalidateQueries({ queryKey: ['payment-request'] }),
    queryClient.invalidateQueries({ queryKey: ['bulk-payment-request'] }),
    queryClient.invalidateQueries({ queryKey: ['bulk-payment-requests'] }),
    queryClient.invalidateQueries({ queryKey: ['pending-payment-approvals'] }),
    queryClient.invalidateQueries({ queryKey: ['expenses'] }),
    queryClient.invalidateQueries({ queryKey: ['expense-stats'] }),
  ]);
};

const getActorName = async (userId: string): Promise<string> => {
  const { data } = await supabase
    .from('user_profiles')
    .select('first_name, last_name')
    .eq('id', userId)
    .single();
  return `${data?.first_name || ''} ${data?.last_name || ''}`.trim() || 'System';
};

const formatCurrency = (amount: number, currency = 'ETB') => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
};

const sendBulkConsolidatedNotifications = async ({
  requests,
  decision,
  actorName,
  reason,
  comments,
}: {
  requests: PaymentRequest[];
  decision: 'approved' | 'rejected' | 'paid';
  actorName: string;
  reason?: string;
  comments?: string;
}) => {
  const count = requests.length;
  const totalAmount = requests.reduce((sum, r) => sum + (r.total_amount || 0), 0);
  const currency = requests[0]?.currency || 'ETB';
  const categories = [...new Set(requests.map(r => r.category?.name).filter(Boolean))];
  const requestNumbers = requests.map(r => r.request_number).sort();
  const firstNum = requestNumbers[0];
  const lastNum = requestNumbers[requestNumbers.length - 1];

  const statusEmoji = decision === 'approved' ? '✅' : decision === 'rejected' ? '❌' : '💰';
  const statusText = decision === 'approved' ? 'Approved' : decision === 'rejected' ? 'Rejected' : 'Payment Confirmed';

  // Consolidated Telegram
  let telegramMsg = `${statusEmoji} Bulk ${statusText} — ${count} Requests\n`;
  telegramMsg += `${decision === 'rejected' ? 'Rejected' : decision === 'paid' ? 'Confirmed' : 'Approved'} by: ${actorName}\n`;
  if (categories.length > 0) telegramMsg += `Category: ${categories.join(', ')}\n`;
  telegramMsg += `Total: ${formatCurrency(totalAmount, currency)}\n`;
  telegramMsg += `Requests: ${firstNum} to ${lastNum}`;
  if (reason) telegramMsg += `\nReason: ${reason}`;

  try {
    // Fetch finance user IDs so notification targets only finance staff, not all users/groups
    const { data: financeUsers } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('role', 'finance');
    const financeUserIds = financeUsers?.map(u => u.id) || [];
    await sendTelegramNotification(telegramMsg, undefined, financeUserIds.length > 0 ? financeUserIds : undefined);
  } catch (e) {
    console.error('Failed to send bulk Telegram notification:', e);
  }

  // Consolidated Email — group by requester email
  const requesterEmail = requests[0]?.requester_email;
  const requesterName = requests[0]?.requester_first_name && requests[0]?.requester_last_name
    ? `${requests[0].requester_first_name} ${requests[0].requester_last_name}`
    : null;

  if (requesterEmail && requesterName) {
    try {
      await supabase.functions.invoke('send-payment-decision-email', {
        body: {
          is_bulk: true,
          requester_name: requesterName,
          requester_email: requesterEmail,
          decision,
          approver_name: actorName,
          currency,
          reason,
          comments,
          bulk_items: requests.map(r => ({
            request_number: r.request_number,
            title: r.title,
            total_amount: r.total_amount,
          })),
        },
      });
    } catch (e) {
      console.error('Failed to send bulk decision email:', e);
    }
  }
};

export const useBulkPaymentRequest = (uploadId: string) => {
  return useQuery({
    queryKey: ['bulk-payment-request', uploadId],
    enabled: !!uploadId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payment_requests')
        .select(`
          *,
          department:departments(*),
          category:expense_categories(*),
          items:payment_request_items(*),
          approvals:payment_request_approvals(*),
          expense:expenses!payment_request_id(
            id,
            expense_number,
            amount,
            vat_rate,
            vat_amount,
            total_amount,
            withholding_rate,
            withholding_amount,
            net_payment
          )
        `)
        .eq('upload_id', uploadId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      return buildBulkGroup((data || []) as unknown as PaymentRequest[]);
    },
  });
};

const fetchAndGroupBulkRequests = async (
  status?: PaymentRequestStatus | PaymentRequestStatus[],
  departmentIds?: string[]
) => {
  let query = supabase
    .from('payment_requests')
    .select(`
      *,
      department:departments(*),
      category:expense_categories(*),
      items:payment_request_items(*),
      approvals:payment_request_approvals(*),
      expense:expenses!payment_request_id(
        id,
        expense_number,
        amount,
        vat_rate,
        vat_amount,
        total_amount,
        withholding_rate,
        withholding_amount,
        net_payment
      )
    `)
    .eq('upload_method', 'bulk_csv')
    .not('upload_id', 'is', null)
    .order('created_at', { ascending: false });

  if (status) {
    if (Array.isArray(status)) {
      query = query.in('status', status);
    } else {
      query = query.eq('status', status);
    }
  }

  if (departmentIds && departmentIds.length > 0) {
    query = query.in('department_id', departmentIds);
  }

  const { data, error } = await query;
  if (error) throw error;

  const grouped = new Map<string, PaymentRequest[]>();

  for (const request of (data || []) as unknown as PaymentRequest[]) {
    if (!request.upload_id) continue;

    const current = grouped.get(request.upload_id) || [];
    current.push(request);
    grouped.set(request.upload_id, current);
  }

  return Array.from(grouped.values())
    .map((group) =>
      [...group].sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      )
    )
    .map(buildBulkGroup)
    .filter((group): group is BulkPaymentRequestGroup => Boolean(group))
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
};

export const useBulkPaymentRequests = (
  status?: PaymentRequestStatus | PaymentRequestStatus[]
) => {
  return useQuery({
    queryKey: ['bulk-payment-requests', status],
    queryFn: () => fetchAndGroupBulkRequests(status),
  });
};

/**
 * Like useBulkPaymentRequests but filtered to only departments
 * where the current user is an active approver.
 */
export const useBulkPaymentRequestsForApprover = (
  status?: PaymentRequestStatus | PaymentRequestStatus[]
) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['bulk-payment-requests-approver', status, user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Get departments where user is an approver
      const { data: approverData, error: approverError } = await supabase
        .from('department_approvers')
        .select('department_id')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (approverError) throw approverError;
      if (!approverData || approverData.length === 0) return [];

      const departmentIds = approverData.map((a) => a.department_id);
      return fetchAndGroupBulkRequests(status, departmentIds);
    },
    enabled: !!user?.id,
  });
};

export const useBulkApprovePaymentRequests = () => {
  const queryClient = useQueryClient();
  const { user, session } = useAuth();
  const userId = user?.id || session?.user?.id;

  return useMutation({
    mutationFn: async ({ requests, comments }: BulkApproveInput) => {
      if (!userId) throw new Error('User is not authenticated');
      if (!requests.length) throw new Error('No requests in this bulk group');

      const invalid = requests.find((r) => r.status !== 'pending_approval');
      if (invalid) {
        throw new Error('Bulk approve is only available when all requests are pending approval');
      }

      for (const request of requests) {
        await approvePaymentRequestAction({
          id: request.id,
          userId,
          comments,
          skipNotifications: true,
        });
      }

      // Send one consolidated notification
      const actorName = await getActorName(userId);
      await sendBulkConsolidatedNotifications({
        requests,
        decision: 'approved',
        actorName,
        comments,
      });

      return requests.length;
    },
    onSuccess: async (count) => {
      await invalidatePaymentRequestQueries(queryClient);
      toast.success(`${count} payment requests approved`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to bulk approve: ${error.message}`);
    },
  });
};

export const useBulkRejectPaymentRequests = () => {
  const queryClient = useQueryClient();
  const { user, session } = useAuth();
  const userId = user?.id || session?.user?.id;

  return useMutation({
    mutationFn: async ({ requests, reason, comments }: BulkRejectInput) => {
      if (!userId) throw new Error('User is not authenticated');
      if (!requests.length) throw new Error('No requests in this bulk group');

      const uniqueStatuses = [...new Set(requests.map((r) => r.status))];
      const allowed =
        uniqueStatuses.length === 1 &&
        (uniqueStatuses[0] === 'pending_approval' || uniqueStatuses[0] === 'approved');

      if (!allowed) {
        throw new Error(
          'Bulk reject is only available when all requests are pending approval or all are approved'
        );
      }

      for (const request of requests) {
        await rejectPaymentRequestAction({
          id: request.id,
          userId,
          reason,
          comments,
          skipNotifications: true,
        });
      }

      // Send one consolidated notification
      const actorName = await getActorName(userId);
      await sendBulkConsolidatedNotifications({
        requests,
        decision: 'rejected',
        actorName,
        reason,
        comments,
      });

      return requests.length;
    },
    onSuccess: async (count) => {
      await invalidatePaymentRequestQueries(queryClient);
      toast.success(`${count} payment requests rejected`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to bulk reject: ${error.message}`);
    },
  });
};

export const useBulkConfirmPaymentRequests = () => {
  const queryClient = useQueryClient();
  const { user, session } = useAuth();
  const userId = user?.id || session?.user?.id;

  return useMutation({
    mutationFn: async ({ payments }: BulkConfirmPaymentRequestsInput) => {
      if (!userId) throw new Error('User is not authenticated');
      if (!payments.length) throw new Error('No payments to confirm');

      const paymentIds = payments.map((payment) => payment.id);

      const { data: requests, error } = await supabase
        .from('payment_requests')
        .select('*, category:expense_categories(*)')
        .in('id', paymentIds);

      if (error) throw error;

      const approvedRequests = (requests || []).filter((request) => request.status === 'approved');
      if (!approvedRequests.length) {
        throw new Error('No approved requests found to confirm');
      }

      for (const payment of payments) {
        if (!payment.bankAccountId) {
          throw new Error('Paid From Account is required for every payment');
        }

        await confirmPaymentAction({
          id: payment.id,
          userId,
          paymentReference: payment.paymentReference,
          actualPaymentDate: payment.actualPaymentDate,
          bankAccountId: payment.bankAccountId,
          baseAmount: payment.baseAmount,
          vatRate: payment.vatRate,
          vatAmount: payment.vatAmount,
          totalExpense: payment.totalExpense,
          withholdingRate: payment.withholdingRate,
          withholdingAmount: payment.withholdingAmount,
          netPayment: payment.netPayment,
          skipNotifications: true,
        });
      }

      // Send one consolidated notification
      const actorName = await getActorName(userId);
      await sendBulkConsolidatedNotifications({
        requests: (requests || []) as unknown as PaymentRequest[],
        decision: 'paid',
        actorName,
      });

      return payments.length;
    },
    onSuccess: async (count) => {
      await invalidatePaymentRequestQueries(queryClient);
      toast.success(`${count} payments confirmed and expenses recorded`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to bulk confirm payments: ${error.message}`);
    },
  });
};

export const useBulkConfirmPayments = useBulkConfirmPaymentRequests;
